<script lang="ts" module>
	import fathersFamilyClub from "@assets/images/fathers-family-club.png?w=300&format=webp&imagetools";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { ClassValue } from "svelte/elements";
	import { getSubscriptionPagePath } from "../../../core/schema/paths/getSubscriptionPagePath.js";
	import plus from "@core/assets/icons/plus.svg?raw";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { SubscriptionParams } from "@core/schema/Subscription.js";
	import { SubscriptionFrequency } from "@core/schema/SubscriptionFrequency.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { SubscriptionSelection } from "@core/schema/SubscriptionSelection.js";
	import { SubscriptionSize } from "@core/schema/SubscriptionSize.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { calculatePriceOfSubscription } from "@core/schema/utils/calculatePriceOfSubscription.js";
	import { getSubscriptionSelectionsForVariant } from "@core/schema/utils/getSubscriptionSelectionsForVariant.js";
	import { formatNumber } from "@core/utils/formatNumber.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { defaultDestinationCountry } from "../defaultDestinationCountry.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import ProductTags from "./ProductTags.svelte";
	import translations from "./SubscriptionCard.translations.json" with { type: "json" };
	import Heading from "./common/Heading.svelte";
	import Icon from "./common/Icon.svelte";
	import Media from "./common/Media.svelte";
</script>

<script lang="ts">
	const { tenant, subcategories, mothersBlendProduct, cafeinaProduct, exchangeRatesSetting } = getGlobalTemplateProps();
	const { MOTHERS_BLEND_CONSTANT_PRICE_ENABLED } = getGlobalSettings();
	const {
		variant,
		image,
		class: customClass,
		restrictSelectionsToVariant = false,
	}: {
		variant: SubscriptionVariant;
		image: { default: string; md: string };
		class?: ClassValue;
		restrictSelectionsToVariant?: boolean;
	} = $props();
	const translate = getTranslate(translations);
	const currency = $derived(selectedCurrency.current ?? tenant.defaultCurrency);

	const prices = $derived.by(() => {
		if (!Cart.loaded) {
			return [0];
		}

		return Object.values(SubscriptionSize).flatMap((size) =>
			Object.values(SubscriptionFrequency).flatMap((frequency) =>
				(restrictSelectionsToVariant
					? getSubscriptionSelectionsForVariant(variant)
					: Object.values(SubscriptionSelection)
				).map((selection) => {
					const subscriptionParams: SubscriptionParams = {
						variant,
						size,
						frequency,
						kind: SubscriptionKind.Recurring,
						selection,
						startsAt: "",
					};

					const { withTax } = calculatePriceOfSubscription({
						subcategories,
						destinationCountry: Cart.destinationCountry ?? defaultDestinationCountry,
						subscription: subscriptionParams,
						mothersBlendProduct,
						cafeinaProduct,
						currency,
						vatType: EconomicSubjectVatType.NotVatSubject,
						exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
						mothersBlendConstantPriceEnabled: MOTHERS_BLEND_CONSTANT_PRICE_ENABLED,
						deliveryCount: undefined,
					});

					return withTax;
				}),
			),
		);
	});

	const minPrice = $derived(Math.min(...prices));
	const maxPrice = $derived(Math.max(...prices));

	async function openConfigurator(): Promise<void> {
		if (!Auth.signedIn) {
			await Auth.openAuthenticationModal(AuthenticationModalKind.Register, {});
			return;
		}
		if (!Cart.state?.hasSubscription) {
			Cart.saveSubscriptionFields({
				variant,
				...(variant === SubscriptionVariant.Filter && { selection: SubscriptionSelection.FathersChoice }),
			});
		}
		Cart.openSubscriptionConfigurator(SubscriptionKind.Recurring);
	}
</script>

<div class={["subscription-card", "flex flex-col w-full max-w-100 md:max-w-full m-auto", customClass]}>
	<div
		class="relative p-4 flex flex-col justify-between w-full aspect-square md:aspect-[unset] min-h-81.5 lg:max-h-81.5 xl:min-h-0 xl:max-h-81.5 md:grow rounded-2xl overflow-hidden"
	>
		<ProductTags
			tags={{ [translate("subscription")]: { class: "bg-fcrbeige font-antipol !text-fcrdarkgray" } }}
			class="pointer-events-none"
		/>
		<Media
			alt=""
			sourcesets={{ ">md": image.md }}
			width={648}
			height={313}
			source={{ src: image.default, type: "image/webp" }}
			wrapperClass="size-full object-cover absolute inset-0"
			class="size-full object-cover absolute inset-0 max-lg:brightness-75"
		/>
		<Media
			alt=""
			sourcesets={{}}
			width={257}
			height={241}
			source={{ src: fathersFamilyClub, type: "image/webp" }}
			wrapperClass="absolute left-1/2 -translate-x-1/2 lg:translate-x-0 lg:left-[unset] lg:right-17 top-1/2 -translate-y-1/2"
		/>
		<a href={getSubscriptionPagePath(tenant)} class="absolute inset-0" tabindex="-1" aria-hidden="true"></a>
		<button
			type="button"
			class={[
				"self-end relative z-10 size-12",
				"bg-fcrdarkgray flex items-center justify-center rounded-full",
				"hover:bg-fcrbrown-100 [&:hover:active]:scale-97 transition-transform duration-150 ease-in-out",
			]}
			onclick={openConfigurator}
		>
			<Icon icon={plus} class="size-6 text-white" />
		</button>
	</div>
	<Heading
		level={3}
		text={translate("buildYourOwn")}
		class="font-antipol text-fcrdarkgray text-base md:text-1.5xl mt-4 md:mt-6"
	/>
	<div class="mt-1 md:mt-2 text-fcrdarkgray text-sm/normal md:text-base">
		{#if minPrice === maxPrice}
			{formatPrice(maxPrice, currency, tenant.locale, false)}
		{:else}
			{formatNumber(minPrice, tenant.locale, false)} – {formatPrice(maxPrice, currency, tenant.locale, false)}
		{/if}
	</div>
</div>
